import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { FiSearch } from "react-icons/fi";
import { MdDownload } from "react-icons/md";
import thesisData from "../../../data/theses.json";
import research from "../../../assets/images/RESEARCH-01.png";

const SOURCE_PDFS = [
  {
    label: "Obstetrics & Gynaecology (updated)",
    href: "/All thesis Title for OBS AND GYNO updated 20256.pdf",
  },
  {
    label: "Obstetrics & Gynaecology Batch 78",
    href: "/Thesis Titles for Obs and gyno Batch 78.pdf",
  },
  {
    label: "Internal Medicine",
    href: "/all thesis title Internal medicine updated 20251.pdf",
  },
  {
    label: "Pediatrics and Child Health",
    href: "/TITLES Pediatrics and Child Health updated 2025.pdf",
  },
  {
    label: "MPCH Batch 7",
    href: "/Thesis Titles MPCH 7.pdf",
  },
];

const PAGE_SIZE = 12;

function matchesQuery(thesis, query) {
  if (!query) return true;
  const haystack = [
    thesis.title,
    thesis.author,
    thesis.program,
    thesis.degree,
    ...(thesis.keywords || []),
  ]
    .join(" ")
    .toLowerCase();
  const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  return terms.every((term) => haystack.includes(term));
}

const Theses = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [program, setProgram] = useState("All");
  const [page, setPage] = useState(1);

  const programs = ["All", ...(thesisData.programs || [])];

  const filtered = useMemo(() => {
    const list = thesisData.theses || [];
    return list.filter((t) => {
      const programOk = program === "All" || t.program === program;
      return programOk && matchesQuery(t, searchTerm);
    });
  }, [program, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const onSearchChange = (value) => {
    setSearchTerm(value);
    setPage(1);
  };

  const onProgramChange = (value) => {
    setProgram(value);
    setPage(1);
  };

  return (
    <>
      <Helmet>
        <title>Postgraduate Thesis Titles | Benadir University Research</title>
        <meta
          name="description"
          content="Search past postgraduate thesis titles from Benadir University by title, author, program, or keyword."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://bu.edu.so/research/theses" />
      </Helmet>

      <div className="mx-7 xs:mx-3">
        <div className="relative w-full">
          <img
            src={research}
            className="w-full h-[20%] bg-cover bg-center"
            alt="Benadir University research"
          />
        </div>

        <div className="flex flex-col w-full text-black my-4 font-montserrat">
          <h1 className="text-2xl md:text-3xl text-[#0863AA] font-semibold">
            Postgraduate Thesis Titles
          </h1>
          <p className="leading-7 text-justify mt-3 max-w-4xl">
            Browse and search thesis titles defended and approved by the School
            of Postgraduate Studies. Search by title, author name, program, or
            keyword to discover related research topics across Obstetrics &amp;
            Gynaecology, Pediatrics and Child Health, and Internal Medicine.
          </p>

          <div className="mt-6 bg-[#EDF4F4] p-4 md:p-5 space-y-4">
            <div className="relative w-full">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0863AA] text-xl" />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search by title, author, or keyword (e.g. preeclampsia, anemia, malnutrition)…"
                className="w-full py-3 pl-11 pr-4 outline-none rounded-md border border-[#c5d5df] focus:border-[#0863AA]"
                aria-label="Search thesis titles"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {programs.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => onProgramChange(p)}
                  className={
                    program === p
                      ? "px-3 py-1.5 text-sm font-semibold bg-[#0863AA] text-white"
                      : "px-3 py-1.5 text-sm font-semibold text-[#0863AA] border border-[#0863AA] hover:bg-[#0863AA] hover:text-white"
                  }
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-row xs:flex-col sm:flex-col justify-between items-baseline mt-5 mb-2 gap-2">
            <p className="text-[#0863AA] font-semibold">
              {filtered.length} {filtered.length === 1 ? "result" : "results"}
              {searchTerm ? ` for “${searchTerm.trim()}”` : ""}
            </p>
            <p className="text-sm text-gray-600">
              {thesisData.theses?.length || 0} titles indexed · updated{" "}
              {thesisData.updatedAt}
            </p>
          </div>

          <div className="divide-y divide-[#d7e2ea] border-t border-b border-[#d7e2ea]">
            {pageItems.length === 0 ? (
              <div className="py-10 text-center text-gray-600">
                No thesis titles match your search. Try fewer keywords or another
                program filter.
              </div>
            ) : (
              pageItems.map((thesis) => (
                <article key={thesis.id} className="py-5">
                  <h2 className="text-lg md:text-xl font-semibold text-[#0863AA] leading-snug">
                    {thesis.title}
                  </h2>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700">
                    <span>
                      <span className="font-semibold text-gray-900">Author:</span>{" "}
                      {thesis.author}
                    </span>
                    <span>
                      <span className="font-semibold text-gray-900">Program:</span>{" "}
                      {thesis.program}
                    </span>
                    <span>
                      <span className="font-semibold text-gray-900">Degree:</span>{" "}
                      {thesis.degree}
                    </span>
                    {thesis.status && thesis.status !== "Listed" ? (
                      <span>
                        <span className="font-semibold text-gray-900">
                          Status:
                        </span>{" "}
                        {thesis.status}
                      </span>
                    ) : null}
                  </div>
                  {thesis.keywords?.length ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {thesis.keywords.slice(0, 8).map((kw) => (
                        <button
                          key={`${thesis.id}-${kw}`}
                          type="button"
                          onClick={() => onSearchChange(kw)}
                          className="text-xs px-2 py-1 border border-[#0863AA]/40 text-[#0863AA] hover:bg-[#0863AA] hover:text-white"
                          title={`Search keyword: ${kw}`}
                        >
                          {kw}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </article>
              ))
            )}
          </div>

          {totalPages > 1 ? (
            <div className="flex justify-center items-center gap-3 my-6">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-4 py-2 border border-[#0863AA] text-[#0863AA] disabled:opacity-40 hover:bg-[#0863AA] hover:text-white"
              >
                Previous
              </button>
              <span className="text-sm font-semibold text-[#0863AA]">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-4 py-2 border border-[#0863AA] text-[#0863AA] disabled:opacity-40 hover:bg-[#0863AA] hover:text-white"
              >
                Next
              </button>
            </div>
          ) : null}

          <div className="mt-8 mb-6">
            <h3 className="text-xl font-semibold text-[#0863AA] mb-2">
              Source documents
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Official postgraduate thesis title lists from the School of
              Postgraduate Studies.
            </p>
            <ul className="space-y-2">
              {SOURCE_PDFS.map((pdf) => (
                <li key={pdf.href}>
                  <a
                    href={pdf.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#0863AA] hover:underline font-medium"
                  >
                    <MdDownload />
                    {pdf.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default Theses;
